import { useQuery } from '@tanstack/react-query'

import { get } from '@/lib/api'

// ==================== Types ====================

export type WorkloadLevel = 'low' | 'normal' | 'high' | 'overloaded'
export type LocalTrackType = 'frontend' | 'backend'

export interface AssignableMember {
  id: string
  name: string
  avatar: string | null
  teamRole: string | null
  activeTaskCount: number
  estimatedRemainingHours: number
  workloadLevel: WorkloadLevel
}

export interface AssignableMembersParams {
  teamId: string
  trackType?: LocalTrackType
  teamRole?: string  // 按团队角色筛选（如 product, design, frontend, backend, testing）
  search?: string
  excludeIds?: string[]
}

// ==================== Query Keys ====================

export const assignableMembersKeys = {
  all: ['assignable-members'] as const,
  list: (params: AssignableMembersParams) => [...assignableMembersKeys.all, params] as const,
  suggestions: (teamId: string, trackType?: LocalTrackType) =>
    [...assignableMembersKeys.all, 'suggestions', teamId, trackType] as const,
}

// ==================== Hooks ====================

/**
 * 获取可分配成员列表
 * Requirements: 1.1, 1.3, 1.5
 */
export function useAssignableMembers(params: AssignableMembersParams) {
  const { teamId, trackType, teamRole, search, excludeIds } = params

  return useQuery({
    queryKey: assignableMembersKeys.list(params),
    queryFn: () =>
      get<AssignableMember[]>(`/pm/teams/${teamId}/assignable-members`, {
        trackType,
        teamRole,
        search,
        excludeIds: excludeIds?.join(','),
      }),
    enabled: !!teamId,
  })
}

/**
 * 获取分配建议（按工作量排序）
 * Requirements: 7.5
 */
export function useAssignmentSuggestions(teamId: string, trackType?: LocalTrackType) {
  return useQuery({
    queryKey: assignableMembersKeys.suggestions(teamId, trackType),
    queryFn: () =>
      get<AssignableMember[]>(`/pm/teams/${teamId}/assignment-suggestions`, {
        trackType,
      }),
    enabled: !!teamId,
  })
}
